/* =============================================================================
   TTW motion v5.2: the ONE shared behaviour script (deferred, theme.liquid).
   The motion itself is native CSS (assets/ttw-award.css). This file only
   decides which path each element takes, releases it, and runs the floats and
   the card → PDP view-transition hook. No motion library.

   PUBLISHED CONTRACT (other streams consume these names exactly)
   ---------------------------------------------------------------
   [data-ttw-reveal], [data-reveal]
       Rise + fade on entry. With scroll-driven animations it runs on view()
       and the element gets .is-in at once, so a legacy section rule such as
       :not(.is-in){opacity:0} can never hide it (sections still delete those
       rules). Otherwise, or when an ancestor is overflow hidden|auto|scroll
       (it would capture view()), the IntersectionObserver path runs: the
       element gets [data-ttw-io] and .is-in releases it on entry.
       Tip: use overflow:clip on section roots. It clips the same way and keeps
       the scroll-driven path.
   [data-ttw-kinetic]
       "Engraved rise": the heading rises through a clip mask and its italic
       accent (the <em> / __f child) follows ~8% later. On an <h1> it is a
       time-based entrance at load that animates transform and clip-path
       only, never opacity. Elsewhere it is scroll-driven like a reveal.
   [data-ttw-parallax="N"]
       Scroll-linked drift of ±N/2 % of its own height (default 12).
   [data-ttw-draw]
       On an <svg> (every path/shape in it) or on single paths. Shapes need
       pathLength="1". The strokes draw on the svg's view(); change the window
       with style="--draw-range: entry 10% cover 40%". Inside a clipping
       ancestor it draws once, as it enters, instead.
   [data-ttw-hero-cta]
       The page's primary hero CTA. The mobile CTA bar stays down until every
       marked CTA is out of view, so two identical gold buttons never share a
       screen. With no marker on the page the bar shows at once.
   html[data-ttw-bar="mcta"|"pdp"] and --ttw-float-lift
       Set by a sticky bottom bar while it is visible; the floats read them.
       The mobile CTA bar is handled here (its lift comes from CSS). The PDP
       bar sets "pdp" and --ttw-float-lift itself.
   [data-vt-card] (snippets/ttw-product-card) + [data-vt="product-hero"] (PDP)
       Card → PDP morph: on pageswap to that product, the clicked card's media
       is named ttw-product-hero; ttw-award.css names the PDP image the same.
   .ttw-media-slot__num
       Plate numerals ("Pl. IV"), numbered here in reading order.
   [data-ttw-count], video[data-ttw-video]
       Count-up and lazy background video, API unchanged (see below).

   Horizon replaces DOM nodes after inline scripts run, so everything here is
   idempotent, rescans on shopify:section:load, a MutationObserver binds nodes
   inserted later, and clicks are delegated at document level.
   ============================================================================= */
(function () {
  'use strict';
  var W = window, doc = document, root = doc.documentElement;
  if (W.__ttwMotion) return;
  W.__ttwMotion = true;

  var reduced = W.TTW_MOTION_OFF === true || root.hasAttribute('data-ttw-motion-off');
  try { reduced = reduced || W.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  var sda = !reduced && !!(W.CSS && CSS.supports && CSS.supports('animation-timeline: view()'));
  var GEOM = 'path,circle,ellipse,line,polyline,polygon,rect';

  root.classList.add('ttw-motion');

  /* ---------- 1. Would an ancestor capture view()? ---------- */
  function isScroller(n) {
    if (n.__ttwSc === undefined) {
      var cs = W.getComputedStyle(n);
      n.__ttwSc = /hidden|auto|scroll/.test(cs.overflowX + ' ' + cs.overflowY);
    }
    return n.__ttwSc;
  }
  /* The outermost overflow hidden|auto|scroll ancestor below <body>, or null. */
  function outerClip(el) {
    var out = null, body = doc.body;
    for (var n = el.parentElement; n && n !== body && n !== root; n = n.parentElement) {
      if (isScroller(n)) out = n;
    }
    return out;
  }
  function inView(el) {
    var r = el.getBoundingClientRect(), h = W.innerHeight || root.clientHeight;
    return (r.width > 0 || r.height > 0) && r.bottom > 0 && r.top < h * 0.94;
  }

  /* ---------- 2. Observers (null = unsupported → safe fallbacks) ---------- */
  var io = null, countIO = null, videoIO = null;
  if ('IntersectionObserver' in W) {
    try {
      io = new IntersectionObserver(function (es) {
        for (var i = 0; i < es.length; i++) {
          if (es[i].isIntersecting) { es[i].target.classList.add('is-in'); io.unobserve(es[i].target); }
        }
        /* v5.3 (3.6). The bottom margin was -6%, which SHRINKS the root box and
           so releases a reveal LATER than it enters the viewport. Combined with
           the callback being async, an element could be wholly on screen and
           still sit at the paused 0% keyframe (opacity 0) - measured on home's
           HOME-SOURCE block at 0.142 (390) and 0.269 (1440) while fully in
           view. It resolved on the next callback, so this was a blank-content
           flash on a fast scroll rather than a dead element, but the rule is
           that nothing may be invisible while it is on screen.
           A POSITIVE bottom margin grows the root box downwards instead, so an
           element is released just before it arrives and its 0.7s rise is
           already finished by the time the reader reaches it. */
      }, { threshold: 0, rootMargin: '0px 0px 15% 0px' });
      countIO = new IntersectionObserver(function (es) {
        for (var i = 0; i < es.length; i++) {
          if (es[i].isIntersecting) { runCount(es[i].target); countIO.unobserve(es[i].target); }
        }
      }, { threshold: 0.4 });
      videoIO = new IntersectionObserver(function (es) {
        for (var i = 0; i < es.length; i++) {
          var v = es[i].target;
          if (es[i].isIntersecting) {
            loadVideo(v);
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        }
      }, { threshold: 0.5 });
    } catch (e) {
      io = countIO = videoIO = null;
    }
  }

  /* ---------- 3. Reveals and kinetic headings ---------- */
  /* The IntersectionObserver path. The start state comes from the attribute,
     and only this script sets it. Already on screen: shown as is. */
  function ioPath(el) {
    if (reduced || !io) { el.classList.add('is-in'); return; }
    if (inView(el)) { el.setAttribute('data-ttw-io', '0'); el.classList.add('is-in'); return; }
    el.setAttribute('data-ttw-io', '');
    io.observe(el);
  }
  function bindReveal(el) {
    if (el.__ttwR) return;
    el.__ttwR = 1;
    if (sda && !outerClip(el)) { el.classList.add('is-in'); return; }
    ioPath(el);
  }
  function bindKinetic(el) {
    if (el.__ttwK) return;
    el.__ttwK = 1;
    /* An h1 runs its own time-based entrance in CSS; it never waits. */
    if (el.tagName === 'H1' || (sda && !outerClip(el))) { el.classList.add('is-in'); return; }
    ioPath(el);
  }

  /* ---------- 4. Parallax ---------- */
  function bindParallax(el) {
    if (el.__ttwP) return;
    el.__ttwP = 1;
    var n = parseFloat(el.getAttribute('data-ttw-parallax'));
    if (!isNaN(n)) el.style.setProperty('--ttw-px', String(n));
    if (!sda) return;
    var host = outerClip(el);
    if (host) { host.setAttribute('data-ttw-host', ''); el.setAttribute('data-ttw-host-tl', ''); }
  }

  /* ---------- 5. Engraving draws ---------- */
  var drawn = [];
  function measureDraw(svg) {
    var vb = svg.viewBox && svg.viewBox.baseVal, r = svg.getBoundingClientRect(), s = 1;
    if (vb && vb.width && vb.height && r.width && r.height) s = Math.min(r.width / vb.width, r.height / vb.height);
    var g = svg.querySelector(GEOM);
    /* With vector-effect: non-scaling-stroke, Chromium measures a
       pathLength-normalised dash in user units but strokes it in screen
       pixels, so a dash of 1 covers only 1/scale of the line (measured:
       2/3 at 1.5x). Stretch the dash by the scale. The animation fills
       backwards only, so once it is through, no dasharray remains: a wrong
       guess can change the timing, never leave a line undrawn. */
    var nss = g && W.getComputedStyle(g).vectorEffect === 'non-scaling-stroke';
    svg.style.setProperty('--ttw-dk', Math.max(1, (nss ? s : 1) * 1.05).toFixed(3));
  }
  function bindDraw(el) {
    var svg = el.closest ? el.closest('svg') : null;
    if (!svg || svg.__ttwD) return;
    svg.__ttwD = 1;
    measureDraw(svg);
    drawn.push(svg);
    if (reduced) return;
    if (sda && !outerClip(svg)) return;     /* CSS draws it on the svg's own view() */
    if (!io) return;                        /* no observer: stays fully drawn */
    svg.setAttribute('data-ttw-io', '');
    if (inView(svg)) svg.classList.add('is-in');   /* on screen: draw now */
    else io.observe(svg);
  }

  /* ---------- 6. Plate numerals ---------- */
  var ROMAN = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  function roman(n) {
    var s = '';
    for (var i = 0; i < ROMAN.length; i++) while (n >= ROMAN[i][0]) { s += ROMAN[i][1]; n -= ROMAN[i][0]; }
    return s;
  }
  function numberPlates() {
    var nums = doc.querySelectorAll('.ttw-media-slot__num'), k = 0;
    for (var i = 0; i < nums.length; i++) {
      if (nums[i].hasAttribute('data-plate-fixed')) continue;
      var slot = nums[i].parentElement;
      if (!slot || !slot.getClientRects().length) continue;   /* not rendered at this width */
      nums[i].textContent = 'Pl. ' + roman(++k);
    }
  }

  /* ---------- 7. Count-up ----------
     data-to="30" (required) · data-decimals="1" · data-prefix · data-suffix ·
     data-comma · data-duration="1400". Markup carries the FINAL value, so
     no-JS visitors see the real number. */
  function fmt(n, o) {
    var s = n.toFixed(o.dec);
    if (o.comma) {
      var p = s.split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      s = p.join('.');
    }
    return o.prefix + s + o.suffix;
  }
  function runCount(el) {
    if (el.__ttwCounting) return;
    el.__ttwCounting = true;
    var to = parseFloat(el.getAttribute('data-to'));
    if (isNaN(to)) return;
    var o = {
      dec: parseInt(el.getAttribute('data-decimals') || '0', 10) || 0,
      prefix: el.getAttribute('data-prefix') || '',
      suffix: el.getAttribute('data-suffix') || '',
      comma: el.hasAttribute('data-comma') && el.getAttribute('data-comma') !== 'false'
    };
    var dur = parseInt(el.getAttribute('data-duration') || '1400', 10) || 1400;
    if (reduced) { el.textContent = fmt(to, o); return; }
    var start = null, raf = null, done = false;
    function finish() {
      if (done) return;
      done = true;
      if (raf !== null) cancelAnimationFrame(raf);
      el.textContent = fmt(to, o);   /* exact final value, always */
    }
    function step(ts) {
      if (done) return;
      if (start === null) start = ts;
      var t = Math.min((ts - start) / dur, 1);
      el.textContent = fmt(to * (1 - Math.pow(1 - t, 3)), o);
      if (t < 1) raf = requestAnimationFrame(step);
      else finish();
    }
    /* CRITICAL: start via requestAnimationFrame(step) so frame 1 gets a real
       timestamp (never step() with no argument: NaN). The timeout guarantees
       the final value even when rAF is paused in a background tab. */
    raf = requestAnimationFrame(step);
    setTimeout(finish, dur + 250);
  }
  function bindCount(el) {
    if (el.__ttwCount) return;
    el.__ttwCount = true;
    if (reduced || !countIO) { runCount(el); return; }
    countIO.observe(el);
  }

  /* ---------- 8. Lazy background video ----------
     Author muted loop playsinline preload="none" poster="…" with
     <source data-src="…"> (no autoplay, no src). Reduced motion: poster only. */
  /* v8: a <source data-mq="(max-width: 760px)"> is used only when its query
     matches, and only the FIRST matching source is attached. The media attribute
     on <video><source> is not honoured consistently across browsers, so the
     choice is made here: phones get the 9:16 loop, desktop the 16:9 one. */
  function loadVideo(v) {
    if (v.__ttwLoaded) return;
    v.__ttwLoaded = true;
    var ss = v.querySelectorAll('source[data-src]');
    var picked = false;
    for (var i = 0; i < ss.length; i++) {
      var mq = ss[i].getAttribute('data-mq');
      var ok = true;
      if (mq) { try { ok = W.matchMedia(mq).matches; } catch (e) { ok = false; } }
      if (ok && !picked) { ss[i].src = ss[i].getAttribute('data-src'); picked = true; }
      else if (ss[i].parentNode) { ss[i].parentNode.removeChild(ss[i]); }
    }
    if (!ss.length && v.getAttribute('data-src')) v.src = v.getAttribute('data-src');
    v.load();
  }
  /* Save-Data and 2G/3G keep the poster: a background loop is decoration, and
     on a slow link it competes with the page the reader actually came for. */
  var slowNet = false;
  try {
    var c = navigator.connection;
    slowNet = !!(c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || '')));
  } catch (e) {}
  function bindVideo(v) {
    if (v.__ttwVideo) return;
    v.__ttwVideo = true;
    if (reduced || slowNet || !videoIO) return;
    videoIO.observe(v);
  }

  /* ---------- 9. Mobile CTA bar + the float contract ---------- */
  var mctaIO = null, mq = null;
  try { mq = W.matchMedia('(max-width: 1023px)'); } catch (e) {}
  function barOn(bar, on) {
    if (on) bar.setAttribute('data-shown', ''); else bar.removeAttribute('data-shown');
    var cur = root.getAttribute('data-ttw-bar');
    if (on && (!mq || mq.matches)) {
      if (!cur) root.setAttribute('data-ttw-bar', 'mcta');
    } else if (cur === 'mcta') {
      root.removeAttribute('data-ttw-bar');
    }
  }
  function bootMcta() {
    if (mctaIO) { mctaIO.disconnect(); mctaIO = null; }
    var bars = doc.querySelectorAll('[data-ttw-mcta]');
    if (!bars.length) return;
    /* Singleton, re-parented to <body> so position:fixed escapes the header's
       backdrop-filter containing block. */
    var bar = bars[0];
    for (var i = 1; i < bars.length; i++) if (bars[i].parentNode) bars[i].parentNode.removeChild(bars[i]);
    if (bar.parentNode !== doc.body) doc.body.appendChild(bar);
    var ctas = Array.prototype.slice.call(doc.querySelectorAll('[data-ttw-hero-cta]'));
    if (!ctas.length || !('IntersectionObserver' in W)) { barOn(bar, true); return; }
    var seen = ctas.map(function () { return false; });
    mctaIO = new IntersectionObserver(function (es) {
      for (var j = 0; j < es.length; j++) {
        var k = ctas.indexOf(es[j].target);
        if (k > -1) seen[k] = es[j].isIntersecting;
      }
      barOn(bar, seen.indexOf(true) === -1);
    });
    ctas.forEach(function (c) { mctaIO.observe(c); });
  }
  if (mq) {
    var onMq = function () {
      var b = doc.querySelector('[data-ttw-mcta]');
      if (b) barOn(b, b.hasAttribute('data-shown'));
    };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  }

  /* ---------- 10. Card → PDP view transition ---------- */
  var lastCard = null, swapped = null;
  doc.addEventListener('click', function (e) {
    var t = e.target;
    lastCard = t && t.closest ? t.closest('.ttw-pc__link') : null;
  }, true);
  W.addEventListener('pageswap', function (e) {
    var card = lastCard;
    lastCard = null;
    if (!e.viewTransition || !card || !card.isConnected) return;
    var to = e.activation && e.activation.entry && e.activation.entry.url;
    var media = card.querySelector('[data-vt-card]');
    if (!to || !media) return;
    try {
      var dest = new URL(to), from = new URL(card.href, location.href);
      if (dest.origin !== location.origin || dest.pathname !== from.pathname) return;
      if (dest.pathname.indexOf('/products/') === -1) return;
    } catch (err) { return; }
    /* A PDP showing recommendation cards already has a product-hero: stand it
       down, or the duplicate name would cancel the whole transition. */
    var named = Array.prototype.slice.call(doc.querySelectorAll('[data-vt="product-hero"]'));
    for (var i = 0; i < named.length; i++) named[i].style.viewTransitionName = 'none';
    media.style.viewTransitionName = 'ttw-product-hero';
    swapped = named.concat(media);
  });
  /* Back/forward cache: the page may be shown again as it was left. */
  W.addEventListener('pageshow', function (e) {
    if (!e.persisted || !swapped) return;
    for (var i = 0; i < swapped.length; i++) swapped[i].style.viewTransitionName = '';
    swapped = null;
  });

  /* ---------- 11. Init (idempotent) ---------- */
  function each(scope, sel, fn) {
    if (scope.nodeType === 1 && scope.matches(sel)) fn(scope);
    var els = scope.querySelectorAll(sel);
    for (var i = 0; i < els.length; i++) fn(els[i]);
  }
  function scan(scope) {
    each(scope, '[data-ttw-reveal],[data-reveal]', bindReveal);
    each(scope, '[data-ttw-kinetic]', bindKinetic);
    each(scope, '[data-ttw-parallax]', bindParallax);
    each(scope, '[data-ttw-draw]', bindDraw);
    each(scope, '[data-ttw-count]', bindCount);
    each(scope, 'video[data-ttw-video]', bindVideo);
  }
  function remeasure() {
    for (var i = 0; i < drawn.length; i++) if (drawn[i].isConnected) measureDraw(drawn[i]);
  }

  var started = false;
  function initAll() {
    if (started) return;
    started = true;
    scan(doc);
    numberPlates();
    bootMcta();
    /* Last, in the same task as the binding above, so no frame can paint
       with the scroll-driven rules active before an element that needs the
       observer path has been marked. */
    if (sda) root.classList.add('ttw-sda');

    if (W.MutationObserver) {
      new MutationObserver(function (ms) {
        var boot = false, plates = false;
        for (var i = 0; i < ms.length; i++) {
          var ad = ms[i].addedNodes;
          for (var j = 0; j < ad.length; j++) {
            var n = ad[j];
            if (n.nodeType !== 1) continue;
            scan(n);
            if (n.matches('[data-ttw-hero-cta],[data-ttw-mcta]') || n.querySelector('[data-ttw-hero-cta],[data-ttw-mcta]')) boot = true;
            if (n.matches('.ttw-media-slot') || n.querySelector('.ttw-media-slot')) plates = true;
          }
        }
        if (boot) bootMcta();
        if (plates) numberPlates();
      }).observe(doc.body, { childList: true, subtree: true });
    }
  }

  if (doc.readyState !== 'loading') initAll();
  else doc.addEventListener('DOMContentLoaded', initAll);

  function onLoad() { root.classList.add('ttw-ready'); numberPlates(); remeasure(); }
  if (doc.readyState === 'complete') onLoad();
  else W.addEventListener('load', onLoad);

  var rz = null;
  W.addEventListener('resize', function () {
    clearTimeout(rz);
    rz = setTimeout(remeasure, 180);
  }, { passive: true });

  doc.addEventListener('shopify:section:load', function (e) {
    scan(e.target || doc);
    numberPlates();
    bootMcta();
  });
})();
